import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Info } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import InfoHint from "../../components/listing/InfoHint";
import ModalShell from "./ModalShell";

const PRICE_SOURCE_LABELS = {
    manual: "Manuell geändert",
    scan_ek: "Lieferschein-Scan (EK)",
    scan_uvp: "Lieferschein-Scan (UVP)",
    scan_markup: "Scan + Aufschlags-Regel",
    scan_create: "Anlage per Scan",
    backfill: "VKP-Nachtrag aus Scans",
};

export default function ArticleModal({ article, suppliers, articles = [], onClose, onSaved }) {
    const isEdit = !!article?.id;
    const [form, setForm] = useState(() => {
        const base = {
            sku: "", name: "", category: "part", manufacturer: "",
            manufacturer_part_number: "", ean: "", supplier_id: "",
            supplier_sku: "", purchase_price_net: 0, purchase_price_gross: 0,
            sell_price_net: 0, sell_price_gross: 0,
            list_price_gross: 0, vat_rate: 19, stock_qty: 0, min_stock: 0,
            unit: "Stück", location: "", tire_width: "", tire_aspect: "",
            tire_diameter: "", tire_speed_index: "", tire_position: "",
            validity_year: "", validity_month: "", plate_color: "",
            description: "", is_active: true,
            ownership: "own", consignment_supplier_id: "",
            price_bound: false, consignment_discount_percent: "",
            consignment_vk_mode: "",  // "" = globaler Modus, sonst "keep_vk" | "markup"
            source_donor_id: "",
            // Jun-2026 WMS-Erweiterungen
            package_components: [], package_labour_net: 0, package_mode: "ask",
            auto_create_workshop_order: false, manufacturer_action_tracking: false,
            winter_storage_role: "",  // "" | "checkin" | "monthly_fee" | "checkout"
            assembly_aw: 0, is_montage_rate: false,
            is_differenzbesteuerung: false,
            exclusion_rules: { combination_article_ids: [], exclude_from_discounts: false, exclude_from_b2c: false, exclude_from_b2b: false },
            ...article,
        };
        // Normalisierung: gespeicherte Artikel können null statt Default-Objekte enthalten.
        return {
            ...base,
            package_components: Array.isArray(base.package_components) ? base.package_components : [],
            exclusion_rules: base.exclusion_rules || { combination_article_ids: [], exclude_from_discounts: false, exclude_from_b2c: false, exclude_from_b2b: false },
        };
    });
    const [busy, setBusy] = useState(false);
    // Jul-2026 (User-Wunsch) — selten genutzte Spezial-Optionen in EINEM
    // Aufklappbereich; beim Bearbeiten mit aktiven Optionen initial geöffnet.
    const [specialOpen] = useState(() => Boolean(
        article && (String(article.description || "").trim()
            || article.price_bound || article.is_differenzbesteuerung || article.is_montage_rate
            || article.auto_create_workshop_order
            || article.manufacturer_action_tracking || article.winter_storage_role
            || article.exclusion_rules?.exclude_from_discounts || article.exclusion_rules?.exclude_from_b2c
            || article.exclusion_rules?.exclude_from_b2b
            || (article.research_extras && Object.keys(article.research_extras).length)
            || (article.exclusion_rules?.combination_article_ids || []).length)
    ));
    // Jun-2026 (User-Wunsch c) — Lieferanten-/GTIN-Lookup: neuer Artikel mit
    // EAN → Bezeichnung/Hersteller/Preise automatisch aus König-Katalog vorbefüllen.
    const [lookupNote, setLookupNote] = useState("");
    useEffect(() => {
        const code = (article?.ean || "").trim();
        if (isEdit || !code) return;
        let active = true;
        setLookupNote("Suche im König-Katalog…");
        api.get(`/articles/catalog-lookup/${encodeURIComponent(code)}`)
            .then((r) => {
                if (!active) return;
                const d = r.data || {};
                if (d.found) {
                    setForm((f) => ({
                        ...f,
                        name: f.name || d.name || "",
                        manufacturer: f.manufacturer || d.manufacturer || "",
                        manufacturer_part_number: f.manufacturer_part_number || d.manufacturer_part_number || "",
                        vat_rate: d.vat_rate || f.vat_rate,
                        purchase_price_net: Number(f.purchase_price_net) > 0 ? f.purchase_price_net : (d.purchase_price_net || 0),
                        sell_price_gross: Number(f.sell_price_gross) > 0 ? f.sell_price_gross : (d.sell_price_gross || 0),
                        uvp_net: f.uvp_net || d.uvp_net || "",
                    }));
                    setLookupNote(`✓ Aus ${d.source === "upcitemdb" ? "GTIN-Datenbank" : "König-Katalog"} übernommen: ${d.name}`);
                } else {
                    setLookupNote("Kein Katalog-Treffer — bitte manuell ausfüllen.");
                }
            })
            .catch(() => { if (active) setLookupNote(""); });
        return () => { active = false; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const [donors, setDonors] = useState([]);
    // Mehr-Standort Phase 4 — Cross-Branch-Bestand + Umlagerung (nur im Bearbeiten-Modus, nur Org).
    const [orgStock, setOrgStock] = useState(null);
    const [transfers, setTransfers] = useState([]);
    const [tfDir, setTfDir] = useState("out");   // "out" = hierher → Filiale, "in" = Filiale → hierher
    const [tfTo, setTfTo] = useState("");
    const [tfQty, setTfQty] = useState("");
    const [tfBusy, setTfBusy] = useState(false);
    const [buckets, setBuckets] = useState(null);      // Mehrlagerorte: Bestand je Lagerplatz
    const [myLocations, setMyLocations] = useState([]); // eigene Lagerplätze (für Quell-Lagerplatz)
    const [tfSrcLoc, setTfSrcLoc] = useState("");       // optionaler Quell-Lagerplatz
    // Aug-2026 (User-Wunsch): Preis-Historie (EK/VKP) am Artikel
    const [priceHistory, setPriceHistory] = useState([]);
    // Aug-2026 (User-Wunsch): Zu-/Abgänge + Nachbestell-Empfehlung + Auto-Speichern
    const [movements, setMovements] = useState([]);
    const [reorderSug, setReorderSug] = useState(null);
    const [autoSavedAt, setAutoSavedAt] = useState(null);
    const lastSavedRef = useRef(null);
    const skipAutosaveRef = useRef(true);
    useEffect(() => {
        if (!article?.id) return;
        api.get(`/articles/${article.id}/price-history`)
            .then((r) => setPriceHistory(r.data?.items || []))
            .catch(() => {});
        api.get(`/articles/${article.id}/movements`)
            .then((r) => setMovements((r.data?.movements || r.data?.items || (Array.isArray(r.data) ? r.data : [])).slice(0, 25)))
            .catch(() => {});
        api.get(`/articles/${article.id}/reorder-suggestion`)
            .then((r) => setReorderSug(r.data?.eligible ? r.data : null))
            .catch(() => {});
    }, [article?.id]);
    const loadOrgStock = () => {
        if (!article?.id) return;
        api.get(`/articles/${article.id}/org-stock`)
            .then((r) => setOrgStock(r.data?.is_org ? r.data : null))
            .catch(() => {});
    };
    const loadBuckets = () => {
        if (!article?.id) return;
        api.get(`/articles/${article.id}/buckets`)
            .then((r) => setBuckets(r.data))
            .catch(() => {});
    };
    const loadTransfers = () => {
        if (!article?.id) return;
        api.get(`/articles/${article.id}/transfers`)
            .then((r) => setTransfers(r.data?.transfers || []))
            .catch(() => {});
    };
    useEffect(() => {
        let on = true;
        (async () => {
            try {
                const { data } = await api.get("/wms/donors");
                if (on) setDonors(data.donors || []);
            } catch { /* Teilespender optional */ }
            try {
                const { data } = await api.get("/dealer/storage-locations");
                if (on) setMyLocations(Array.isArray(data) ? data : (data?.items || []));
            } catch { /* optional */ }
        })();
        return () => { on = false; };
    }, []);
    useEffect(() => {
        if (!article?.id) return undefined;
        let on = true;
        loadBuckets();
        api.get(`/articles/${article.id}/org-stock`)
            .then((r) => { if (on && r.data?.is_org) { setOrgStock(r.data); loadTransfers(); } })
            .catch(() => {});
        return () => { on = false; };
    }, [article?.id]);

    const submitTransfer = async () => {
        const q = Number(tfQty);
        if (!tfTo) { toast.error("Bitte Gegenstandort wählen"); return; }
        if (!(q > 0)) { toast.error("Menge muss größer als 0 sein"); return; }
        setTfBusy(true);
        try {
            const payload = { counterpart_dealer_id: tfTo, direction: tfDir, quantity: q };
            if (tfDir === "out" && tfSrcLoc) payload.source_location_id = tfSrcLoc;
            const { data } = await api.post(`/articles/${article.id}/transfer`, payload);
            toast.success(`Umlagerung angefragt: ${data.quantity} ${form.unit || "Stück"} (${data.source_site_code} → ${data.target_site_code})`);
            setTfQty("");
            loadTransfers();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Umlagerung fehlgeschlagen");
        } finally { setTfBusy(false); }
    };

    const transferAction = async (tid, action) => {
        setTfBusy(true);
        try {
            await api.post(`/org/transfers/${tid}/${action}`);
            toast.success({ ship: "Versendet — unterwegs", receive: "Empfangen — angekommen", cancel: "Umlagerung storniert" }[action]);
            loadTransfers();
            loadOrgStock();
            loadBuckets();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
        } finally { setTfBusy(false); }
    };
    const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    // Netto ↔ Brutto Live-Berechnung über den aktuellen USt-Satz.
    const setEkNet = (v) => setForm((f) => {
        const factor = 1 + (Number(f.vat_rate) || 0) / 100;
        return { ...f, purchase_price_net: v, purchase_price_gross: v === "" ? "" : round2(Number(v) * factor) };
    });
    const setEkGross = (v) => setForm((f) => {
        const factor = 1 + (Number(f.vat_rate) || 0) / 100;
        return { ...f, purchase_price_gross: v, purchase_price_net: v === "" ? "" : round2(Number(v) / factor) };
    });
    const setVkGross = (v) => setForm((f) => {
        const factor = 1 + (Number(f.vat_rate) || 0) / 100;
        return { ...f, sell_price_gross: v, sell_price_net: v === "" ? "" : round2(Number(v) / factor) };
    });
    // Aug-2026 (User-Wunsch): VK netto auch direkt eingebbar → brutto wird mitgerechnet.
    const setVkNet = (v) => setForm((f) => {
        const factor = 1 + (Number(f.vat_rate) || 0) / 100;
        return { ...f, sell_price_net: v, sell_price_gross: v === "" ? "" : round2(Number(v) * factor) };
    });
    const setVat = (v) => setForm((f) => {
        const factor = 1 + (Number(v) || 0) / 100;
        return {
            ...f,
            vat_rate: v,
            purchase_price_gross: f.purchase_price_net === "" ? f.purchase_price_gross : round2(Number(f.purchase_price_net) * factor),
            sell_price_net: f.sell_price_gross === "" ? f.sell_price_net : round2(Number(f.sell_price_gross) / factor),
        };
    });
    // Jun-2026 — VK-Sonderfälle: UVP-Bindung & Kommissions-Abschlag
    const setPriceBound = (checked) => setForm((f) => {
        if (!checked) return { ...f, price_bound: false };
        const factor = 1 + (Number(f.vat_rate) || 0) / 100;
        const uvp = Number(f.list_price_gross) || 0;
        return {
            ...f, price_bound: true,
            sell_price_gross: uvp > 0 ? uvp : f.sell_price_gross,
            sell_price_net: uvp > 0 ? round2(uvp / factor) : f.sell_price_net,
        };
    });
    const setConsignmentDiscount = (v) => setForm((f) => ({ ...f, consignment_discount_percent: v }));
    // Jun-2026 (User-Wunsch) — Der Kommissions-Abschlag verändert den VK NICHT
    // mehr (früher: VK = EK − X%). Er senkt nur den effektiven EK (bessere Marge).
    // Der VK ergibt sich aus dem VK-Modus (keep_vk = manuell/vorgegeben,
    // markup = Aufschlagsregeln). Effektiver EK rein informativ:
    const effectiveEkNet = (() => {
        const ek = Number(form.purchase_price_net) || 0;
        const disc = Number(form.consignment_discount_percent) || 0;
        if (form.ownership !== "consignment" || !ek || form.consignment_discount_percent === "") return null;
        return round2(ek * (1 - disc / 100));
    })();
    // --- Paket-Komponenten (Jun-2026) ---
    // Jul-2026 (User-Wunsch): durchsuchbare MEHRFACH-Auswahl über den GESAMTEN
    // Artikelkatalog (Server-Suche) statt Einzel-Dropdown mit nur der aktuellen Seite.
    const [pkgSearch, setPkgSearch] = useState("");
    const [pkgResults, setPkgResults] = useState([]);
    const [pkgSearching, setPkgSearching] = useState(false);
    const [pkgLabels, setPkgLabels] = useState({});
    useEffect(() => {
        if (form.category !== "package") return;
        let cancelled = false;
        const t = setTimeout(async () => {
            setPkgSearching(true);
            try {
                const params = new URLSearchParams({ limit: "30" });
                if (pkgSearch.trim()) params.set("search", pkgSearch.trim());
                const r = await api.get(`/articles?${params.toString()}`);
                if (cancelled) return;
                const list = (r.data || []).filter((a) => a.id !== article?.id && a.category !== "package");
                setPkgResults(list);
                setPkgLabels((m) => {
                    const next = { ...m };
                    for (const a of list) next[a.id] = `${a.name} (${a.sku})`;
                    return next;
                });
            } catch { /* best effort */ }
            if (!cancelled) setPkgSearching(false);
        }, 300);
        return () => { cancelled = true; clearTimeout(t); };
    }, [pkgSearch, form.category, article?.id]);
    // Labels für bereits gespeicherte Bestandteile nachladen (Bearbeiten-Fall)
    useEffect(() => {
        if (form.category !== "package") return;
        const missing = (form.package_components || []).map((c) => c.article_id).filter((id) => id && !pkgLabels[id]);
        if (!missing.length) return;
        let cancelled = false;
        (async () => {
            const add = {};
            for (const id of missing) {
                const local = articles.find((a) => a.id === id);
                if (local) { add[id] = `${local.name} (${local.sku})`; continue; }
                try {
                    const r = await api.get(`/articles/${id}`);
                    add[id] = `${r.data.name} (${r.data.sku})`;
                } catch { add[id] = id; }
            }
            if (!cancelled) setPkgLabels((m) => ({ ...m, ...add }));
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.category, form.package_components]);
    const togglePackageArticle = (a) => setForm((f) => {
        const cur = f.package_components || [];
        const exists = cur.some((c) => c.article_id === a.id);
        return {
            ...f,
            package_components: exists
                ? cur.filter((c) => c.article_id !== a.id)
                : [...cur, { article_id: a.id, quantity: 1 }],
        };
    });
    const updatePackageComponent = (idx, key, val) => setForm((f) => {
        const list = [...(f.package_components || [])];
        list[idx] = { ...list[idx], [key]: val };
        return { ...f, package_components: list };
    });
    const removePackageComponent = (idx) => setForm((f) => ({ ...f, package_components: (f.package_components || []).filter((_, i) => i !== idx) }));
    // --- Ausschlussregeln (Jun-2026) ---
    const setExclusion = (key, val) => setForm((f) => ({ ...f, exclusion_rules: { ...(f.exclusion_rules || {}), [key]: val } }));
    const toggleExclusionArticle = (id) => setForm((f) => {
        const cur = f.exclusion_rules?.combination_article_ids || [];
        const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
        return { ...f, exclusion_rules: { ...(f.exclusion_rules || {}), combination_article_ids: next } };
    });
    const otherArticles = articles.filter((a) => a.id !== article?.id);
    const buildPayload = () => {
        const payload = { ...form };
            delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload.dealer_id;
            ["purchase_price_net", "purchase_price_gross", "sell_price_net", "sell_price_gross", "list_price_gross", "vat_rate", "stock_qty", "min_stock", "package_labour_net", "assembly_aw"].forEach((k) => {
                if (payload[k] !== undefined && payload[k] !== "") payload[k] = Number(payload[k]) || 0;
            });
            // Paket-Komponenten: nur gültige Zeilen, Menge numerisch
            if (payload.category === "package") {
                payload.package_components = (form.package_components || [])
                    .filter((c) => c.article_id)
                    .map((c) => ({ article_id: c.article_id, quantity: Number(c.quantity) || 1 }));
            } else {
                payload.package_components = null;
            }
            // Ausschlussregeln immer als Objekt mitsenden
            payload.exclusion_rules = {
                combination_article_ids: form.exclusion_rules?.combination_article_ids || [],
                exclude_from_discounts: !!form.exclusion_rules?.exclude_from_discounts,
                exclude_from_b2c: !!form.exclusion_rules?.exclude_from_b2c,
                exclude_from_b2b: !!form.exclusion_rules?.exclude_from_b2b,
            };
            ["tire_width", "tire_aspect", "tire_diameter"].forEach((k) => {
                if (payload[k] === "" || payload[k] === null) payload[k] = null;
                else payload[k] = Number(payload[k]);
            });
            // Versicherungskennzeichen: Gültigkeitsjahr/-monat numerisch/null, Farbe null wenn leer
            if (payload.validity_year === "" || payload.validity_year === null) payload.validity_year = null;
            else payload.validity_year = Number(payload.validity_year) || null;
            if (payload.validity_month === "" || payload.validity_month === null) payload.validity_month = null;
            else payload.validity_month = Number(payload.validity_month) || null;
            if (!payload.plate_color) payload.plate_color = null;
            if (!payload.supplier_id) payload.supplier_id = null;
            if (payload.ownership !== "consignment" || !payload.consignment_supplier_id) payload.consignment_supplier_id = null;
            // VK-Sonderfälle (Jun-2026): Kommissions-Abschlag nur bei Kommissionsware, sonst null
            if (payload.ownership !== "consignment" || payload.consignment_discount_percent === "" || payload.consignment_discount_percent === null) {
                payload.consignment_discount_percent = null;
            } else {
                payload.consignment_discount_percent = Number(payload.consignment_discount_percent) || 0;
            }
            // VK-Modus für Kommissionsware: "" → globaler Modus (null senden)
            if (payload.ownership !== "consignment" || !payload.consignment_vk_mode) {
                payload.consignment_vk_mode = null;
            }
            payload.price_bound = !!payload.price_bound;
            if (!payload.source_donor_id) payload.source_donor_id = null;
            // Winterlager-Rolle: "" → null
            if (!payload.winter_storage_role) payload.winter_storage_role = null;
            delete payload._donorLabel;
            if (!payload.tire_position) payload.tire_position = null;
        return payload;
    };
    const submit = async (e) => {
        e.preventDefault();
        if (!form.sku?.trim() || !form.name?.trim()) { toast.error("SKU + Name Pflicht"); return; }
        setBusy(true);
        try {
            const payload = buildPayload();
            let saved;
            if (isEdit) {
                const r = await api.patch(`/articles/${article.id}`, payload);
                saved = r.data;
            } else {
                const r = await api.post("/articles", payload);
                saved = r.data;
            }
            toast.success(isEdit ? "Artikel aktualisiert" : "Artikel angelegt");
            onSaved(saved);
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
        setBusy(false);
    };
    // Aug-2026 (User-Wunsch): Auto-Speichern im Bearbeiten-Modus (1,2 s nach
    // der letzten Änderung) — der „Aktualisieren"-Button entfällt.
    useEffect(() => {
        if (!isEdit) return undefined;
        if (skipAutosaveRef.current) { skipAutosaveRef.current = false; return undefined; }
        if (!form.sku?.trim() || !form.name?.trim()) return undefined;
        const t = setTimeout(async () => {
            try {
                const r = await api.patch(`/articles/${article.id}`, buildPayload());
                lastSavedRef.current = r.data;
                setAutoSavedAt(new Date());
            } catch (err) {
                toast.error(err.response?.data?.detail || "Auto-Speichern fehlgeschlagen");
            }
        }, 1200);
        return () => clearTimeout(t);
    }, [form]); // eslint-disable-line react-hooks/exhaustive-deps
    // Beim Schließen: auto-gespeicherte Änderungen an die Liste zurückmelden.
    const handleClose = () => {
        if (lastSavedRef.current) onSaved(lastSavedRef.current);
        else onClose();
    };
    return (
        <ModalShell onClose={handleClose} title={isEdit ? "Artikel bearbeiten" : "Neuer Artikel"} wide testid="article-modal">
            <form onSubmit={submit} className="space-y-3">
                {lookupNote && (
                    <div className={`text-xs rounded-sm px-3 py-2 border ${lookupNote.startsWith("✓") ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-blue-50 border-blue-200 text-blue-800"}`} data-testid="art-lookup-note">
                        {lookupNote}
                    </div>
                )}
                <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="SKU / Artikel-Nr *" value={form.sku} onChange={(e) => setF("sku", e.target.value)} className="rounded-sm font-mono-num" data-testid="art-sku" />
                    <Select value={form.category} onValueChange={(v) => setF("category", v)}>
                        <SelectTrigger className="rounded-sm" data-testid="art-cat"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="part">Ersatzteile</SelectItem>
                            <SelectItem value="tire">Reifen</SelectItem>
                            <SelectItem value="accessory">Zubehör</SelectItem>
                            <SelectItem value="fluid">Flüssigkeit</SelectItem>
                            <SelectItem value="labour">Lohn (AW)</SelectItem>
                            <SelectItem value="insurance_plate">Versicherungskennzeichen</SelectItem>
                            <SelectItem value="sales">Verkauf/Vertrieb</SelectItem>
                            <SelectItem value="miet_ersatzfahrzeug">Miet / Ersatzfahrzeug</SelectItem>
                            <SelectItem value="package">Paket / Set</SelectItem>
                            <SelectItem value="manufacturer_action">Herstelleraktion</SelectItem>
                            <SelectItem value="gw_damage">GW-Preisliste</SelectItem>
                            <SelectItem value="festpreis">Festpreise</SelectItem>
                            <SelectItem value="werkstatt_service">Werkstatt-Servicepreise</SelectItem>
                            <SelectItem value="other">Sonstiges</SelectItem>
                        </SelectContent>
                    </Select>
                    <select
                        value={form.unit || "Stück"}
                        onChange={(e) => setF("unit", e.target.value)}
                        className="h-9 rounded-sm border border-gray-300 px-2 text-sm bg-white"
                        data-testid="art-unit-select"
                    >
                        {["Stück", "Liter", "Stunde",
                            ...(form.unit && !["Stück", "Liter", "Stunde"].includes(form.unit) ? [form.unit] : [])]
                            .map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                </div>
                <Input placeholder="Bezeichnung *" value={form.name} onChange={(e) => setF("name", e.target.value)} className="rounded-sm" data-testid="art-name" />
                <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="Hersteller" value={form.manufacturer || ""} onChange={(e) => setF("manufacturer", e.target.value)} className="rounded-sm" />
                    <Input placeholder="Hersteller-Teile-Nr." value={form.manufacturer_part_number || ""} onChange={(e) => setF("manufacturer_part_number", e.target.value)} className="rounded-sm" />
                    <Input placeholder="EAN/GTIN" value={form.ean || ""} onChange={(e) => setF("ean", e.target.value)} className="rounded-sm" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Select value={form.supplier_id || "none"} onValueChange={(v) => setF("supplier_id", v === "none" ? "" : v)}>
                        <SelectTrigger className="rounded-sm" data-testid="art-supplier"><SelectValue placeholder="Lieferant wählen…" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">— ohne Lieferant —</SelectItem>
                            {suppliers.filter((s) => s.is_active).map((s) => (
                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Input placeholder="Bestell-Nr beim Lieferanten" value={form.supplier_sku || ""} onChange={(e) => setF("supplier_sku", e.target.value)} className="rounded-sm" />
                </div>

                <div className="space-y-3">
                    {/* Einkauf — EK netto ↔ EK brutto (Zwischen-Titel entfernt, Jul-2026 User-Wunsch) */}
                    <div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold inline-flex items-center gap-1">EK netto € <InfoHint id="wms.ek_net" defaultText="Einkaufspreis ohne USt." side="bottom" /></label>
                                <Input type="number" min="0" step="0.01" value={form.purchase_price_net} onChange={(e) => setEkNet(e.target.value)} className="rounded-sm font-mono-num" data-testid="art-ek-net" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold inline-flex items-center gap-1">USt % <InfoHint id="wms.vat_rate" defaultText="Umsatzsteuersatz dieses Artikels (19 % Standard, 7 % ermäßigt, 0 % steuerfrei). Steuert die Netto-/Brutto-Umrechnung." side="bottom" /></label>
                                <Select value={String(form.vat_rate)} onValueChange={setVat}>
                                    <SelectTrigger className="rounded-sm" data-testid="art-vat"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="19">19 %</SelectItem>
                                        <SelectItem value="7">7 %</SelectItem>
                                        <SelectItem value="0">0 %</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    {/* Verkauf — Zwischen-Titel entfernt (Jul-2026 User-Wunsch); UVP brutto
                        wandert bei Hersteller-Kalkulation in die Preis-Herkunft-Box. */}
                    <div>
                        <div className="grid grid-cols-3 gap-2">
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold inline-flex items-center gap-1">VK brutto € <InfoHint id="wms.vk_gross" defaultText="Verkaufspreis inkl. USt, den der Kunde zahlt. Daraus wird der VK netto automatisch berechnet." side="bottom" /></label>
                                <Input type="number" min="0" step="0.01" value={form.sell_price_gross} onChange={(e) => setVkGross(e.target.value)} readOnly={!!form.price_bound} className={`rounded-sm font-mono-num ${form.price_bound ? "bg-gray-100 text-gray-600" : ""}`} data-testid="art-vk-gross" />
                            </div>
                            {/* Aug-2026 (User-Wunsch): UVP brutto IMMER anzeigen */}
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold inline-flex items-center gap-1">UVP brutto € <InfoHint id="wms.uvp_gross" defaultText="Unverbindliche Preisempfehlung des Herstellers (inkl. USt) — nur zur Orientierung, fließt nicht in die Marge ein." side="bottom" /></label>
                                <Input type="number" min="0" step="0.01" value={form.list_price_gross} onChange={(e) => { setF("list_price_gross", e.target.value); if (form.price_bound) setPriceBound(true); }} className="rounded-sm font-mono-num" data-testid="art-uvp-gross" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold inline-flex items-center gap-1">VK netto € <InfoHint id="wms.vk_net" defaultText="Verkaufspreis ohne USt — direkt eingebbar; VK brutto wird automatisch über den USt-Satz mitberechnet (und umgekehrt)." side="bottom" /></label>
                                <Input type="number" value={form.sell_price_net} onChange={(e) => setVkNet(e.target.value)} className="rounded-sm font-mono-num" data-testid="art-vk-net" />
                            </div>
                        </div>
                        {/* UVP-gebunden & §25a → verschoben in „Spezial-Optionen" (Jul-2026, User-Wunsch) */}
                    </div>
                </div>

                <div className="space-y-2">
                    {/* Titel „Lager" entfernt (Jul-2026 User-Wunsch). Bestand · Mindestbestand ·
                        Lagerort · Eigentum in EINER kompakten Zeile. */}
                    <div className={`grid ${form.ownership === "consignment" ? "grid-cols-5" : "grid-cols-4"} gap-2`}>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Bestand</label>
                            <Input type="number" step="0.01" value={form.stock_qty} onChange={(e) => setF("stock_qty", e.target.value)} className="rounded-sm h-8 text-xs" data-testid="art-stock" disabled={isEdit} />
                            {isEdit && <div className="text-[9px] text-gray-500 mt-0.5">Über „Lager“ buchen</div>}
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold inline-flex items-center gap-1">Mindestbestand <InfoHint id="wms.min_stock" defaultText="Schwelle für die Nachbestell-Warnung. Unterschreitet der Bestand diesen Wert, wird der Artikel als „nachbestellen“ markiert." side="bottom" /></label>
                            <Input type="number" min="0" step="0.01" value={form.min_stock} onChange={(e) => setF("min_stock", e.target.value)} className="rounded-sm h-8 text-xs" data-testid="art-min" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Lagerort</label>
                            <Input placeholder="z. B. Regal A3" value={form.location || ""} onChange={(e) => setF("location", e.target.value)} className="rounded-sm h-8 text-xs" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold inline-flex items-center gap-1">
                                Eigentum
                                <InfoHint id="wms.ownership" defaultText="Eigenbestand = im Lagerwert bilanziert. Kommissionsware = gehört dem Lieferanten, wird erst bei Verkauf bezahlt; zählt NICHT zum Lagerwert und löst beim Verkauf automatisch eine Lieferantenverbindlichkeit aus." side="bottom" />
                            </label>
                            <Select value={form.ownership || "own"} onValueChange={(v) => setF("ownership", v)}>
                                <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="art-ownership"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="own">Eigenbestand</SelectItem>
                                    <SelectItem value="consignment">Kommissionsware</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    {/* Aug-2026 (User-Wunsch): Lagerplätze + Standorte (Org) in EINEM
                        eingeklappten Block; eigener Bestand oben, Kommission markiert. */}
                    {((buckets && (buckets.buckets || []).length > 0) || (orgStock && (orgStock.locations || []).length > 1)) && (
                    <details className="border border-[#0EA5E9]/30 bg-[#0EA5E9]/5 rounded-sm mt-1" data-testid="art-stock-details">
                        <summary className="cursor-pointer select-none px-2.5 py-2 flex items-center justify-between">
                            <span className="text-[10px] uppercase tracking-wider font-bold text-[#0369A1]">Bestand: Lagerplätze &amp; Standorte (Org) — aufklappen</span>
                            <span className="text-[11px] font-bold font-mono-num text-[#0369A1]" data-testid="art-org-stock-total">
                                Σ {orgStock?.total_stock ?? buckets?.total_stock ?? form.stock_qty} {form.unit || "Stück"}
                            </span>
                        </summary>
                        <div className="px-2.5 pb-2.5">
                    {buckets && (buckets.buckets || []).length > 0 && (
                        <div className="border border-teal-200 bg-teal-50/50 rounded-sm p-2.5 mt-1" data-testid="art-buckets">
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="text-[10px] uppercase tracking-wider font-bold text-teal-700">Bestand je Lagerplatz</span>
                                <span className="text-[11px] font-bold font-mono-num text-teal-700" data-testid="art-buckets-total">Σ {buckets.total_stock} {form.unit || "Stück"}</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {buckets.buckets.map((b, i) => (
                                    <span key={b.location_id || `u${i}`} className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-sm border ${b.location_id ? "border-teal-300 bg-white text-teal-800" : "border-gray-300 bg-gray-50 text-gray-500"}`} data-testid={`art-bucket-${b.location_id || "none"}`}>
                                        <span className="font-bold">{b.code}</span>
                                        <span className="font-mono-num">{b.qty}</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {/* Bestand an anderen Standorten (Cross-Branch) */}
                    {orgStock && (orgStock.locations || []).length > 1 && (
                        <div className="rounded-sm p-2.5 mt-1" data-testid="art-org-stock">
                            <div className="space-y-0.5">
                                {orgStock.locations.map((loc) => (
                                    <div key={loc.dealer_id} className="flex items-center justify-between text-[11px]" data-testid={`art-org-stock-row-${loc.dealer_id}`}>
                                        <span className="truncate">
                                            <span className="font-bold text-[#111827]">{loc.site_code}</span>
                                            {loc.is_self && <span className="text-[#FFCC00] ml-1" title="Eigener Standort">★</span>}
                                            <span className="text-gray-500 ml-1">{loc.company_name}</span>
                                            {loc.location && <span className="text-teal-700 ml-1 font-mono-num" title="Lagerort">· {loc.location}</span>}
                                            {(loc.city || loc.street) && (
                                                <span className="text-gray-400 ml-1" title="Adresse">· {[loc.street, loc.city].filter(Boolean).join(", ")}</span>
                                            )}
                                            {loc.ownership === "consignment" && (
                                                <span className="ml-1 text-[9px] font-bold uppercase bg-orange-50 text-orange-800 border border-orange-200 rounded-sm px-1 py-0.5" data-testid={`art-org-consignment-${loc.dealer_id}`}>Kommission</span>
                                            )}
                                        </span>
                                        <span className={`font-mono-num font-bold shrink-0 ml-2 ${loc.stock_qty > 0 ? "text-emerald-700" : "text-gray-400"}`}>
                                            {loc.stock_qty}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            {/* Umlagern (von/zu diesem Standort) */}
                            <div className="mt-2 pt-2 border-t border-[#0EA5E9]/20 flex flex-wrap items-center gap-1.5" data-testid="art-transfer-form">
                                <span className="text-[10px] uppercase tracking-wider font-bold text-[#0369A1]">Umlagern</span>
                                <select
                                    value={tfDir}
                                    onChange={(e) => setTfDir(e.target.value)}
                                    className="h-7 rounded-sm border border-gray-300 bg-white text-[11px] px-1"
                                    data-testid="art-transfer-dir"
                                >
                                    <option value="out">hierher → Standort</option>
                                    <option value="in">Standort → hierher</option>
                                </select>
                                <select
                                    value={tfTo}
                                    onChange={(e) => setTfTo(e.target.value)}
                                    className="h-7 rounded-sm border border-gray-300 bg-white text-[11px] px-1 max-w-[150px]"
                                    data-testid="art-transfer-target"
                                >
                                    <option value="">— Gegenstandort —</option>
                                    {orgStock.locations.filter((l) => (article?.dealer_id ? l.dealer_id !== article.dealer_id : !l.is_self)).map((l) => (
                                        <option key={l.dealer_id} value={l.dealer_id}>
                                            {l.site_code} · {l.company_name} ({l.stock_qty})
                                        </option>
                                    ))}
                                </select>
                                <Input
                                    type="number" min="0" step="0.01" placeholder="Menge"
                                    value={tfQty} onChange={(e) => setTfQty(e.target.value)}
                                    className="h-7 w-20 rounded-sm text-[11px]"
                                    data-testid="art-transfer-qty"
                                />
                                {tfDir === "out" && myLocations.length > 0 && (
                                    <select
                                        value={tfSrcLoc}
                                        onChange={(e) => setTfSrcLoc(e.target.value)}
                                        className="h-7 rounded-sm border border-gray-300 bg-white text-[11px] px-1 max-w-[150px]"
                                        data-testid="art-transfer-src-loc"
                                        title="Optionaler Quell-Lagerplatz"
                                    >
                                        <option value="">Quelle: beliebig</option>
                                        {myLocations.map((l) => (
                                            <option key={l.id} value={l.id}>ab {l.code}</option>
                                        ))}
                                    </select>
                                )}
                                <Button
                                    type="button" onClick={submitTransfer} disabled={tfBusy || !tfTo || !(Number(tfQty) > 0)}
                                    className="h-7 bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-bold rounded-sm text-[11px] px-2"
                                    data-testid="art-transfer-submit"
                                >
                                    {tfBusy ? "…" : "Anfragen"}
                                </Button>
                            </div>
                            {/* Offene/letzte Umlagerungen für diese SKU (Status-Workflow) */}
                            {transfers.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-[#0EA5E9]/20 space-y-1" data-testid="art-transfer-list">
                                    {transfers.slice(0, 6).map((tr) => {
                                        const badge = {
                                            requested: { t: "angefragt", c: "bg-amber-100 text-amber-800" },
                                            in_transit: { t: "unterwegs", c: "bg-blue-100 text-blue-800" },
                                            arrived: { t: "angekommen", c: "bg-emerald-100 text-emerald-800" },
                                            stored: { t: "eingelagert", c: "bg-teal-100 text-teal-800" },
                                            cancelled: { t: "storniert", c: "bg-gray-100 text-gray-500" },
                                        }[tr.status] || { t: tr.status, c: "bg-gray-100 text-gray-600" };
                                        return (
                                            <div key={tr.id} className="flex items-center gap-1.5 text-[11px]" data-testid={`art-transfer-item-${tr.id}`}>
                                                <span className={`px-1.5 rounded-sm font-bold uppercase tracking-wider text-[9px] ${badge.c}`} data-testid={`art-transfer-status-${tr.id}`}>{badge.t}</span>
                                                <span className="font-mono-num font-bold">{tr.quantity}</span>
                                                <span className="text-gray-600 truncate">{tr.source_site_code} → {tr.target_site_code}</span>
                                                <span className="ml-auto flex gap-1 shrink-0">
                                                    {tr.status === "requested" && (
                                                        <button type="button" onClick={() => transferAction(tr.id, "ship")} disabled={tfBusy} className="px-1.5 py-0.5 rounded-sm bg-blue-600 text-white font-bold text-[10px] disabled:opacity-50" data-testid={`art-transfer-ship-${tr.id}`}>Versenden</button>
                                                    )}
                                                    {tr.status === "in_transit" && (
                                                        <button type="button" onClick={() => transferAction(tr.id, "receive")} disabled={tfBusy} className="px-1.5 py-0.5 rounded-sm bg-emerald-600 text-white font-bold text-[10px] disabled:opacity-50" data-testid={`art-transfer-receive-${tr.id}`}>Empfangen</button>
                                                    )}
                                                    {(tr.status === "requested" || tr.status === "in_transit") && (
                                                        <button type="button" onClick={() => transferAction(tr.id, "cancel")} disabled={tfBusy} className="px-1.5 py-0.5 rounded-sm border border-red-300 text-red-600 font-bold text-[10px] disabled:opacity-50" data-testid={`art-transfer-cancel-${tr.id}`}>Storno</button>
                                                    )}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                        </div>
                    </details>
                    )}
                    {form.ownership === "consignment" ? (
                        <div className="grid grid-cols-3 gap-2 pt-1 items-end">
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold inline-flex items-center gap-1">
                                    Abschlag % (aufs EK)
                                    <InfoHint id="wms.consignment_discount" defaultText="Fester Abschlag in % auf den EK-Netto. Senkt den effektiven EK (bessere Marge) — verändert den VK NICHT. Leer = globaler Kommissions-Abschlag aus der VK-Kalkulation." side="bottom" />
                                </label>
                                <Input type="number" min="0" max="100" step="0.1" value={form.consignment_discount_percent} onChange={(e) => setConsignmentDiscount(e.target.value)} placeholder="z. B. 20" className="rounded-sm font-mono-num" data-testid="art-consignment-discount" />
                                {effectiveEkNet != null && (
                                    <div className="text-[10px] text-emerald-800 mt-1" data-testid="art-effective-ek">
                                        Effektiver EK netto: <strong className="font-mono-num">{effectiveEkNet.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</strong>
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold inline-flex items-center gap-1">
                                    VK-Modus
                                    <InfoHint id="wms.consignment_vk_mode" defaultText="Wie wird der VK für diese Kommissionsware ermittelt? VK vorgeben = der eingegebene/UVP-VK bleibt. Aufschlag auf EK = VK über die Aufschlagsregeln auf den vollen EK. Global = der in der VK-Kalkulation eingestellte Standard." side="bottom" />
                                </label>
                                <Select value={form.consignment_vk_mode || "global"} onValueChange={(v) => setF("consignment_vk_mode", v === "global" ? "" : v)}>
                                    <SelectTrigger className="rounded-sm" data-testid="art-consignment-vk-mode"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="global">Global (Standard)</SelectItem>
                                        <SelectItem value="keep_vk">VK vorgeben (bleibt)</SelectItem>
                                        <SelectItem value="markup">Aufschlag auf EK</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div />
                        </div>
                    ) : null}
                    {donors.length > 0 && (
                        <div className="pt-1">
                            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold inline-flex items-center gap-1">
                                Herkunft (Teilespender)
                                <InfoHint id="wms.source_donor" defaultText="Aus welchem Teilespender (Fahrzeug zum Zerlegen, Zustand Schrott/Verwertung/Export) wurde dieses Teil ausgebaut? Optional — verknüpft das Teil mit dem Spenderfahrzeug." side="bottom" />
                            </label>
                            <Select value={form.source_donor_id || "none"} onValueChange={(v) => setF("source_donor_id", v === "none" ? "" : v)}>
                                <SelectTrigger className="rounded-sm" data-testid="art-source-donor"><SelectValue placeholder="— ohne —" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">— ohne —</SelectItem>
                                    {donors.map((d) => (
                                        <SelectItem key={d.listing_id} value={d.listing_id}>
                                            {[d.brand, d.model, d.year].filter(Boolean).join(" ")}{d.vin ? ` · ${d.vin}` : ""}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>

                {form.category === "tire" && (
                    <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 space-y-2">
                        <div className="text-xs font-bold uppercase tracking-wider text-amber-900">Reifen-Daten</div>
                        <div className="grid grid-cols-5 gap-2">
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">Breite</label>
                                <Input type="number" min="0" max="400" value={form.tire_width || ""} onChange={(e) => setF("tire_width", e.target.value)} className="rounded-sm" placeholder="180" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">Quersch.</label>
                                <Input type="number" min="0" max="100" value={form.tire_aspect || ""} onChange={(e) => setF("tire_aspect", e.target.value)} className="rounded-sm" placeholder="55" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">Zoll</label>
                                <Input type="number" min="0" max="30" value={form.tire_diameter || ""} onChange={(e) => setF("tire_diameter", e.target.value)} className="rounded-sm" placeholder="17" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">SI</label>
                                <Input value={form.tire_speed_index || ""} onChange={(e) => setF("tire_speed_index", e.target.value)} className="rounded-sm" placeholder="ZR" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">Position</label>
                                <Select value={form.tire_position || "any"} onValueChange={(v) => setF("tire_position", v === "any" ? "" : v)}>
                                    <SelectTrigger className="rounded-sm"><SelectValue placeholder="—" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="any">— egal —</SelectItem>
                                        <SelectItem value="front">Vorn</SelectItem>
                                        <SelectItem value="rear">Hinten</SelectItem>
                                        <SelectItem value="both">Beide</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                )}

                {form.category === "insurance_plate" && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 space-y-2" data-testid="art-insurance-plate-block">
                        <div className="text-xs font-bold uppercase tracking-wider text-emerald-900">Versicherungskennzeichen</div>
                        <div className="grid grid-cols-3 gap-2">
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Gültig ab Monat *</label>
                                <Select value={form.validity_month ? String(form.validity_month) : "none"} onValueChange={(v) => setF("validity_month", v === "none" ? "" : v)}>
                                    <SelectTrigger className="rounded-sm" data-testid="art-validity-month"><SelectValue placeholder="— Monat —" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">— Monat —</SelectItem>
                                        {["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"].map((m, i) => (
                                            <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Gültigkeitsjahr *</label>
                                <Input type="number" min="2000" max="2100" value={form.validity_year || ""} onChange={(e) => setF("validity_year", e.target.value)} className="rounded-sm font-mono-num" placeholder={String(new Date().getFullYear())} data-testid="art-validity-year" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Farbe (wechselt jährlich)</label>
                                <Select value={form.plate_color || "none"} onValueChange={(v) => setF("plate_color", v === "none" ? "" : v)}>
                                    <SelectTrigger className="rounded-sm" data-testid="art-plate-color"><SelectValue placeholder="— Farbe —" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">— Farbe —</SelectItem>
                                        <SelectItem value="blau">Blau</SelectItem>
                                        <SelectItem value="gruen">Grün</SelectItem>
                                        <SelectItem value="schwarz">Schwarz</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        {form.validity_year && Number(form.validity_year) < new Date().getFullYear() && (
                            <div className="text-[11px] font-bold text-red-700" data-testid="art-plate-expired-hint">
                                ⚠ Abgelaufen (Gültigkeitsjahr {form.validity_year}). Bitte aktuelle Kennzeichen nachbestellen.
                            </div>
                        )}
                    </div>
                )}

                {/* Beschreibung → verschoben in „Spezial-Optionen" (Jul-2026, User-Wunsch) */}

                {/* Paket / Set — Bestandteile + Arbeitslohn + Übernahme-Modus */}
                {form.category === "package" && (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-3 space-y-2" data-testid="art-package-block">
                        <div className="text-xs font-bold uppercase tracking-wider text-indigo-900">Paket-Bestandteile</div>
                        {/* Jul-2026 (User-Wunsch): Suche über den GESAMTEN Katalog + Mehrfachauswahl per Checkbox */}
                        <Input
                            placeholder="Artikel suchen (SKU, Name, EAN) — Mehrfachauswahl per Häkchen"
                            value={pkgSearch}
                            onChange={(e) => setPkgSearch(e.target.value)}
                            className="rounded-sm bg-white"
                            data-testid="art-package-search"
                        />
                        <div className="max-h-44 overflow-y-auto border border-indigo-200 rounded-sm bg-white divide-y divide-gray-100" data-testid="art-package-results">
                            {pkgSearching && <div className="px-3 py-2 text-xs text-gray-400">Suche…</div>}
                            {!pkgSearching && pkgResults.length === 0 && (
                                <div className="px-3 py-2 text-xs text-gray-400">Keine Artikel gefunden.</div>
                            )}
                            {pkgResults.map((a) => {
                                const checked = (form.package_components || []).some((c) => c.article_id === a.id);
                                return (
                                    <label key={a.id} className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-800 cursor-pointer hover:bg-black/5">
                                        <input type="checkbox" checked={checked} onChange={() => togglePackageArticle(a)} data-testid={`art-package-pick-${a.id}`} />
                                        <span className="flex-1">{a.name} <span className="text-gray-400 font-mono-num">({a.sku})</span></span>
                                        {Number(a.sell_price_gross) > 0 && <span className="font-mono-num text-gray-500">{Number(a.sell_price_gross).toFixed(2)} €</span>}
                                    </label>
                                );
                            })}
                        </div>
                        {(form.package_components || []).length > 0 && (
                            <div className="space-y-1.5 pt-1">
                                <div className="text-[10px] uppercase tracking-wider text-indigo-800 font-bold">Gewählte Bestandteile ({(form.package_components || []).length})</div>
                                {(form.package_components || []).map((comp, idx) => (
                                    <div key={comp.article_id || idx} className="grid grid-cols-12 gap-2 items-center bg-white border border-indigo-100 rounded-sm px-2 py-1" data-testid={`art-package-row-${idx}`}>
                                        <div className="col-span-8 text-xs text-gray-800 truncate" data-testid={`art-package-article-${idx}`}>
                                            {pkgLabels[comp.article_id] || comp.article_id || "—"}
                                        </div>
                                        <div className="col-span-3">
                                            <Input type="number" min="0" step="0.01" value={comp.quantity} onChange={(e) => updatePackageComponent(idx, "quantity", e.target.value)} className="rounded-sm font-mono-num h-8" placeholder="Menge" data-testid={`art-package-qty-${idx}`} />
                                        </div>
                                        <div className="col-span-1 text-right">
                                            <button type="button" onClick={() => removePackageComponent(idx)} className="text-red-600 hover:text-red-800 text-lg font-bold" data-testid={`art-package-remove-${idx}`}>×</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-2 pt-1">
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-indigo-800 font-bold">Arbeitslohn netto €</label>
                                <Input type="number" min="0" step="0.01" value={form.package_labour_net} onChange={(e) => setF("package_labour_net", e.target.value)} className="rounded-sm font-mono-num" data-testid="art-package-labour" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider text-indigo-800 font-bold">Übernahme in Auftrag</label>
                                <Select value={form.package_mode || "ask"} onValueChange={(v) => setF("package_mode", v)}>
                                    <SelectTrigger className="rounded-sm" data-testid="art-package-mode"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="ask">Vorher fragen</SelectItem>
                                        <SelectItem value="lump">Als Sammelposten</SelectItem>
                                        <SelectItem value="exploded">Aufgelöst (Einzelposten)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                )}

                {/* „Werkstattauftrag vorschlagen" → verschoben in „Spezial-Optionen" (Jul-2026, User-Wunsch) */}

                {/* Jun-2026 (User-Wunsch) — Montage-Arbeitswerte (AW) für Zubehör */}
                {form.category === "accessory" && (
                    <div className="bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="art-assembly-aw-block">
                        <label className="text-[10px] uppercase tracking-wider text-amber-800 font-bold inline-flex items-center gap-1">
                            Montage-Arbeitswerte (AW)
                            <InfoHint id="wms.assembly_aw" defaultText="Anzahl Arbeitswerte (AW) für die Montage dieses Zubehörs. In der Disposition wird daraus automatisch eine Montage-Lohnposition berechnet: AW × dynamischer AW-Satz (Lohn/AW-Artikel, der als Standard-Montage-Lohnsatz markiert ist). 0 oder leer = keine automatische Montageberechnung." side="bottom" />
                        </label>
                        <Input type="number" min="0" step="0.1" value={form.assembly_aw} onChange={(e) => setF("assembly_aw", e.target.value)} className="rounded-sm font-mono-num mt-1 w-32" data-testid="art-assembly-aw" />
                        <div className="text-[11px] text-amber-700 mt-1">Wird beim Zubehör in der Disposition automatisch als Montage-Lohn (AW × AW-Satz) ergänzt.</div>
                    </div>
                )}

                {/* Jul-2026 (User-Wunsch) — Preis-Herkunft nach unten verschoben und
                    einklappbar (standardmäßig zugeklappt), damit sie das Formular
                    nicht unterbricht. */}
                {Number(article?.uvp_net) > 0 && article?.rabattcode && (
                    <details className="bg-amber-50 border border-amber-200 rounded-sm" data-testid="art-price-origin">
                        <summary className="text-[10px] uppercase tracking-wider text-amber-700 font-bold cursor-pointer select-none px-3 py-2.5 flex items-center gap-1">
                            <Info className="w-3 h-3" /> Preis-Herkunft (Hersteller-Kalkulation)
                            <span className="font-normal normal-case text-amber-600/70 ml-1">— aufklappen für Details</span>
                        </summary>
                        <div className="px-3 pb-3 pt-1 border-t border-amber-200">
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                                <div>
                                    <div className="text-[10px] text-gray-500 uppercase inline-flex items-center gap-1">UVP netto <InfoHint id="wms.origin_uvp" defaultText="Unverbindliche Preisempfehlung des Herstellers (netto, ohne USt). Stammt aus der importierten Hersteller-Preisliste (z. B. Piaggio) und wird bei jedem Preislisten-Update automatisch aktualisiert." /></div>
                                    <div className="font-mono-num font-bold">{Number(article.uvp_net).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] text-gray-500 uppercase inline-flex items-center gap-1">UVP brutto € <InfoHint id="wms.uvp_gross" defaultText="Unverbindliche Preisempfehlung des Herstellers (inkl. USt) — nur zur Orientierung, fließt nicht in die Marge ein." /></div>
                                    <Input type="number" min="0" step="0.01" value={form.list_price_gross} onChange={(e) => { setF("list_price_gross", e.target.value); if (form.price_bound) setPriceBound(true); }} className="rounded-sm font-mono-num h-7 text-xs bg-white" data-testid="art-uvp-gross" />
                                </div>
                                <div>
                                    <div className="text-[10px] text-gray-500 uppercase inline-flex items-center gap-1">Rabattgruppe <InfoHint id="wms.origin_discount_group" defaultText="Vom Hersteller vergebene Rabattgruppe dieses Artikels samt Markencode (z. B. S · AP). Sie bestimmt über den Discount Grill des Herstellers, welcher Einkaufsrabatt für Sie gilt." /></div>
                                    <div className="font-bold">{article.rabattcode}{article.marke_code ? ` · ${article.marke_code}` : ""}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] text-gray-500 uppercase inline-flex items-center gap-1">Rabatt <InfoHint id="wms.origin_discount" defaultText="Ihr Einkaufsrabatt laut Hersteller-Konditionen (Discount Grill, Klasse A, Normalauftrag) für die Rabattgruppe dieses Artikels. Rechnerisch: Rabatt = 1 − (EK ÷ UVP netto)." /></div>
                                    <div className="font-mono-num font-bold">{(Math.max(0, (1 - Number(article.purchase_price_net) / Number(article.uvp_net))) * 100).toFixed(1)} %</div>
                                </div>
                                <div>
                                    <div className="text-[10px] text-gray-500 uppercase inline-flex items-center gap-1">EK / VK netto <InfoHint id="wms.origin_ekvk" defaultText="EK = UVP netto × (1 − Rabatt) → Ihr Einkaufspreis. VK = UVP netto × 1,3 → kalkulierter Verkaufspreis mit 30 % Aufschlag (Standard-Kalkulation; im Feld VK brutto jederzeit anpassbar)." /></div>
                                    <div className="font-mono-num font-bold">{Number(article.purchase_price_net).toLocaleString("de-DE", { style: "currency", currency: "EUR" })} / {Number(article.sell_price_net).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</div>
                                </div>
                            </div>
                            <p className="text-[10px] text-amber-700/80 mt-2">
                                EK = UVP × (1 − Rabatt) · VK = UVP × 1,3 · Quelle: Piaggio Discount Grill (Klasse A, Normalauftrag)
                            </p>
                        </div>
                    </details>
                )}

                {/* Jul-2026 (User-Wunsch) — Spezial-Optionen: selten genutzte Sonderfälle
                    einheitlich in EINEM Aufklappbereich (standardmäßig zugeklappt). */}
                {(() => {
                    const specialCount = [
                        String(form.description || "").trim(),
                        form.price_bound, form.is_differenzbesteuerung,
                        ["part", "accessory", "tire", "fluid"].includes(form.category) && form.auto_create_workshop_order,
                        form.category === "labour" && form.is_montage_rate,
                        form.category === "manufacturer_action" && form.manufacturer_action_tracking,
                        ["package", "labour", "other"].includes(form.category) && form.winter_storage_role,
                        form.exclusion_rules?.exclude_from_discounts,
                        form.exclusion_rules?.exclude_from_b2c,
                        form.exclusion_rules?.exclude_from_b2b,
                        (form.exclusion_rules?.combination_article_ids || []).length > 0,
                    ].filter(Boolean).length;
                    return (
                <details className="bg-gray-50 border border-gray-200 rounded-sm" data-testid="art-special-options" {...(specialOpen ? { open: true } : {})}>
                    <summary className="text-xs font-bold uppercase tracking-wider text-gray-700 cursor-pointer select-none px-3 py-2.5">
                        Spezial-Optionen <span className="font-normal normal-case text-gray-400">(selten benötigt)</span>
                        {specialCount > 0 && (
                            <span className="ml-2 bg-[#111827] text-[#FFCC00] text-[10px] font-black px-1.5 py-0.5 rounded-sm align-middle" data-testid="art-special-count">{specialCount} aktiv</span>
                        )}
                    </summary>
                    <div className="px-3 pb-3 pt-2 border-t border-gray-200 space-y-3">

                        {/* Beschreibung (optional) */}
                        <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid="art-description-block">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">Beschreibung (optional)</div>
                            <Textarea placeholder="Beschreibung" value={form.description || ""} onChange={(e) => setF("description", e.target.value)} className="rounded-sm min-h-[60px]" data-testid="art-description" />
                        </div>

                        {/* Preis & Steuer */}
                        <div className="bg-white border border-gray-200 rounded-sm p-3 space-y-2">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Preis &amp; Steuer</div>
                            <label className="flex items-start gap-2 text-[11px] text-gray-700 cursor-pointer" data-testid="art-price-bound-label">
                                <input type="checkbox" checked={!!form.price_bound} onChange={(e) => setPriceBound(e.target.checked)} className="mt-0.5" data-testid="art-price-bound" />
                                <span><strong>UVP-gebunden</strong> (kein Aufschlag) — VK = UVP. Für vom Hersteller fest beworbene Preise.</span>
                            </label>
                            <label className="flex items-start gap-2 text-[11px] text-gray-700 cursor-pointer" data-testid="art-diff-tax-label">
                                <input type="checkbox" checked={!!form.is_differenzbesteuerung} onChange={(e) => setF("is_differenzbesteuerung", e.target.checked)} className="mt-0.5" data-testid="art-diff-tax" />
                                <span><strong>§25a Differenzbesteuerung</strong> zulässig — nur dann erscheint im Direktverkauf/Rechnung die §25a-Option für diesen Artikel.</span>
                            </label>
                        </div>

                        {/* Werkstatt-Trigger — bei Ersatzteil/Zubehör/Reifen/Flüssigkeit */}
                        {["part", "accessory", "tire", "fluid"].includes(form.category) && (
                            <label className="flex items-start gap-2 bg-white border border-gray-200 rounded-sm p-3 cursor-pointer" data-testid="art-workshop-trigger-block">
                                <input type="checkbox" checked={!!form.auto_create_workshop_order} onChange={(e) => setF("auto_create_workshop_order", e.target.checked)} className="mt-0.5" data-testid="art-workshop-trigger" />
                                <span className="text-[11px] text-gray-700">
                                    <span className="font-bold">Werkstattauftrag vorschlagen</span> — bei Verwendung dieses Teils an einem Fahrzeug automatisch einen Werkstattauftrag (nach Rückfrage) anlegen.
                                </span>
                            </label>
                        )}

                        {/* Jun-2026 (User-Wunsch) — Standard-Montage-Lohnsatz (AW-Satz) */}
                        {form.category === "labour" && (
                            <label className="flex items-start gap-2 bg-white border border-gray-200 rounded-sm p-3 cursor-pointer" data-testid="art-montage-rate-block">
                                <input type="checkbox" checked={!!form.is_montage_rate} onChange={(e) => setF("is_montage_rate", e.target.checked)} className="mt-0.5" data-testid="art-montage-rate" />
                                <span className="text-[11px] text-gray-700">
                                    <span className="font-bold">Als Standard-Montage-Lohnsatz (AW-Satz) verwenden</span> — der <b>VK brutto</b> dieses Artikels gilt dann als Preis pro Arbeitswert (€/AW). Zubehör mit hinterlegten Montage-AW wird in der Disposition automatisch damit kalkuliert. Es kann nur ein Artikel als Standard-AW-Satz aktiv sein. Hinweis: Der Werkstatt-Stundensatz aus den Einstellungen hat Vorrang.
                                </span>
                            </label>
                        )}

                        {/* Herstelleraktion — Zahlungs-Nachverfolgung */}
                        {form.category === "manufacturer_action" && (
                            <label className="flex items-start gap-2 bg-white border border-gray-200 rounded-sm p-3 cursor-pointer" data-testid="art-manufacturer-action-block">
                                <input type="checkbox" checked={!!form.manufacturer_action_tracking} onChange={(e) => setF("manufacturer_action_tracking", e.target.checked)} className="mt-0.5" data-testid="art-manufacturer-action-tracking" />
                                <span className="text-[11px] text-gray-700">
                                    <span className="font-bold">Hersteller-Zahlung nachverfolgen</span> — erzeugt bei Auslösung einen Dispo-Vermerk / eine interne Rechnung zur Nachverfolgung der Herstellerzahlung.
                                </span>
                            </label>
                        )}

                        {/* Winterlager-Rolle (Jun-2026, User-Wunsch) — für Paket-/Dienstleistungs-Artikel */}
                        {["package", "labour", "other"].includes(form.category) && (
                            <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid="art-winter-storage-block">
                                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold inline-flex items-center gap-1">
                                    Winterlager-Rolle
                                    <InfoHint id="wms.winter_storage_role" defaultText="Markiert diesen Artikel für den Winterlager-Workflow. Bei Abschluss eines Werkstattauftrags mit diesem Artikel: Einlagerung = Fahrzeug kommt ins Winterlager · Standgebühr/Monat = Monatsbetrag (Brutto) für die Standzeit · Auslagerung = Fahrzeug wird mit Datum ausgelagert." side="bottom" />
                                </label>
                                <Select value={form.winter_storage_role || "none"} onValueChange={(v) => setF("winter_storage_role", v === "none" ? "" : v)}>
                                    <SelectTrigger className="rounded-sm mt-1" data-testid="art-winter-storage-role"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">— keine —</SelectItem>
                                        <SelectItem value="checkin">Winter-Einlagerung</SelectItem>
                                        <SelectItem value="monthly_fee">Monatliche Standgebühr (Preis = Monatsbetrag)</SelectItem>
                                        <SelectItem value="checkout">Winter-Auslagerung</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Jul-2026 (User-Wunsch) — gespeicherte Zusatz-Infos aus der KI-Recherche */}
                        {article?.research_extras && Object.keys(article.research_extras).length > 0 && (
                            <div className="bg-white border border-gray-200 rounded-sm p-3 text-xs" data-testid="art-research-extras">
                                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">Zusatz-Infos aus KI-Recherche</div>
                                <div className="space-y-1 text-gray-700">
                                    {article.research_extras.dimensions && <div><strong>Maße:</strong> {article.research_extras.dimensions}</div>}
                                    {article.research_extras.weight && <div><strong>Gewicht:</strong> {article.research_extras.weight}</div>}
                                    {article.research_extras.materials && <div><strong>Materialien:</strong> {article.research_extras.materials}</div>}
                                    {article.research_extras.tags?.length > 0 && <div><strong>Tags:</strong> {article.research_extras.tags.join(", ")}</div>}
                                    {article.research_extras.compatibility?.length > 0 && <div><strong>Kompatibel mit:</strong> {article.research_extras.compatibility.join(", ")}</div>}
                                    {article.research_extras.technical_specs && (
                                        <pre className="bg-gray-50 p-2 rounded-sm overflow-x-auto text-[11px]">{JSON.stringify(article.research_extras.technical_specs, null, 2)}</pre>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Ausschlussregeln — v. a. Dienstleistungen/Verkauf */}
                        <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid="art-exclusion-block">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Ausschlussregeln</div>
                            <div className="space-y-2">
                                <div className="flex flex-col gap-1.5">
                                    <label className="flex items-center gap-2 text-xs text-gray-800 cursor-pointer">
                                        <input type="checkbox" checked={!!form.exclusion_rules?.exclude_from_discounts} onChange={(e) => setExclusion("exclude_from_discounts", e.target.checked)} data-testid="art-excl-discounts" />
                                        Von Plattform-/Aktions-Rabatten ausnehmen
                                    </label>
                                    <label className="flex items-center gap-2 text-xs text-gray-800 cursor-pointer">
                                        <input type="checkbox" checked={!!form.exclusion_rules?.exclude_from_b2c} onChange={(e) => setExclusion("exclude_from_b2c", e.target.checked)} data-testid="art-excl-b2c" />
                                        Nicht im B2C-Shop anbieten
                                    </label>
                                    <label className="flex items-center gap-2 text-xs text-gray-800 cursor-pointer">
                                        <input type="checkbox" checked={!!form.exclusion_rules?.exclude_from_b2b} onChange={(e) => setExclusion("exclude_from_b2b", e.target.checked)} data-testid="art-excl-b2b" />
                                        Nicht im B2B-Netzwerk anbieten
                                    </label>
                                </div>
                                {otherArticles.length > 0 && (
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Darf nicht gemeinsam beauftragt werden mit:</div>
                                        <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-sm bg-white p-2 space-y-1">
                                            {otherArticles.map((a) => (
                                                <label key={a.id} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                                                    <input type="checkbox" checked={(form.exclusion_rules?.combination_article_ids || []).includes(a.id)} onChange={() => toggleExclusionArticle(a.id)} data-testid={`art-excl-combo-${a.id}`} />
                                                    {a.name} <span className="text-gray-400 font-mono-num">({a.sku})</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </details>
                    );
                })()}

                {/* Aug-2026 (User-Wunsch): Preis-Historie + Bewegungen eingeklappt */}
                {isEdit && (
                    <details className="border border-gray-200 rounded-sm bg-gray-50/60" data-testid="art-price-history">
                        <summary className="cursor-pointer select-none px-3 py-2 text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                            Preis-Historie (EK / VKP) &amp; Zugänge / Abgänge — aufklappen
                        </summary>
                        <div className="px-3 pb-3">
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">Preis-Historie (EK / VKP)</div>
                        {priceHistory.length === 0 ? (
                            <p className="text-[11px] text-gray-400">Noch keine Preisänderungen protokolliert — ab jetzt wird jede EK-/VKP-Änderung mit Datum und Quelle festgehalten.</p>
                        ) : (
                            <ul className="space-y-1 max-h-36 overflow-y-auto">
                                {priceHistory.map((h) => (
                                    <li key={h.id} className="text-[11px] text-gray-700 flex flex-wrap items-baseline gap-x-2" data-testid={`art-price-history-${h.id}`}>
                                        <span className="text-gray-400 font-mono-num shrink-0">{h.created_at ? new Date(h.created_at).toLocaleDateString("de-DE") : "—"}</span>
                                        <span className="font-bold">{h.field === "purchase_price_net" ? "EK netto" : "VKP brutto"}</span>
                                        <span className="font-mono-num">
                                            {h.old != null ? `${Number(h.old).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € → ` : ""}
                                            <b>{Number(h.new).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</b>
                                        </span>
                                        <span className="text-gray-400">{PRICE_SOURCE_LABELS[h.source] || h.source}{h.reference ? ` · ${h.reference}` : ""}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {/* Aug-2026 (User-Wunsch): Zu- & Abgänge des Artikels */}
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mt-3 mb-1.5">Zugänge &amp; Abgänge</div>
                        {movements.length === 0 ? (
                            <p className="text-[11px] text-gray-400">Noch keine Lagerbewegungen.</p>
                        ) : (
                            <ul className="space-y-1 max-h-36 overflow-y-auto" data-testid="art-movements-list">
                                {movements.map((m) => (
                                    <li key={m.id} className="text-[11px] text-gray-700 flex flex-wrap items-baseline gap-x-2">
                                        <span className="text-gray-400 font-mono-num shrink-0">{m.created_at ? new Date(m.created_at).toLocaleDateString("de-DE") : "—"}</span>
                                        <span className={`font-bold ${m.movement_type === "in" ? "text-emerald-700" : m.movement_type === "out" ? "text-red-700" : "text-gray-700"}`}>
                                            {m.movement_type === "in" ? "Zugang" : m.movement_type === "out" ? "Abgang" : m.movement_type === "inventory" ? "Inventur" : "Korrektur"}
                                        </span>
                                        <span className="font-mono-num font-bold">{m.movement_type === "out" ? "−" : "+"}{Math.abs(Number(m.quantity ?? m.delta ?? 0)).toLocaleString("de-DE", { maximumFractionDigits: 2 })}</span>
                                        <span className="text-gray-400 truncate">{[m.reference, m.note].filter(Boolean).join(" · ")}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        </div>
                    </details>
                )}

                {isEdit ? (
                    <div className="text-[11px] text-center py-1 font-bold text-emerald-700" data-testid="art-autosave-hint">
                        {autoSavedAt
                            ? <>✓ Automatisch gespeichert um {autoSavedAt.toLocaleTimeString("de-DE")}</>
                            : <span className="text-gray-500 font-normal">Änderungen werden automatisch gespeichert</span>}
                    </div>
                ) : (
                <Button type="submit" disabled={busy} className="w-full bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider" data-testid="art-submit">
                    {busy ? "Speichere…" : "Artikel anlegen"}
                </Button>
                )}
            </form>
        </ModalShell>
    );
}
